import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { catchError, Observable, Subject, switchMap, take, filter, throwError } from 'rxjs';
import { AuthService } from 'app/core/auth/auth.service';
import { AuthUtils } from 'app/core/auth/auth.utils';

@Injectable()
export class AuthInterceptor implements HttpInterceptor
{
    private isRefreshing = false;
    private refreshDone$ = new Subject<boolean>();

    /**
     * Constructor
     */
    constructor(private _authService: AuthService)
    {
    }

    /**
     * Intercept
     *
     * @param req
     * @param next
     */
    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>>
    {
        // Clone the request and add credentials for cookie-based refresh
        let newReq = req.clone({ withCredentials: true });

        // If the access token didn't expire, add the Authorization header.
        if ( this._authService.accessToken && !AuthUtils.isTokenExpired(this._authService.accessToken) )
        {
            newReq = newReq.clone({
                headers: newReq.headers.set('Authorization', 'Bearer ' + this._authService.accessToken)
            });
        }

        // Response
        return next.handle(newReq).pipe(
            catchError((error) => {

                // Catch "401 Unauthorized" responses
                if ( error instanceof HttpErrorResponse && error.status === 401 )
                {
                    const isRefreshRequest = req.url.includes('refresh-token');

                    // If the refresh-token request itself failed, sign out
                    if (isRefreshRequest)
                    {
                        this._authService.signOut();
                        location.reload();
                        return throwError(() => error);
                    }

                    return this.handleTokenRefresh(newReq, next);
                }

                return throwError(() => error);
            })
        );
    }

    private handleTokenRefresh(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>>
    {
        if (!this.isRefreshing)
        {
            this.isRefreshing = true;

            return this._authService.signInUsingToken().pipe(
                switchMap((success: boolean) => {
                    this.isRefreshing = false;
                    this.refreshDone$.next(success);

                    if (!success)
                    {
                        this._authService.signOut();
                        location.reload();
                        return throwError(() => 'Token refresh failed');
                    }

                    // Retry with new token
                    const retryReq = request.clone({
                        headers: request.headers.set('Authorization', 'Bearer ' + this._authService.accessToken)
                    });
                    return next.handle(retryReq);
                }),
                catchError((err) => {
                    this.isRefreshing = false;
                    this.refreshDone$.next(false);
                    this._authService.signOut();
                    location.reload();
                    return throwError(() => err);
                })
            );
        }

        // If a refresh is already in progress, wait for it then retry
        return this.refreshDone$.pipe(
            take(1),
            filter(success => success),
            switchMap(() => {
                const retryReq = request.clone({
                    headers: request.headers.set('Authorization', 'Bearer ' + this._authService.accessToken)
                });
                return next.handle(retryReq);
            })
        );
    }
}
